import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  doc, 
  query, 
  where,
  deleteDoc,
  setDoc,
  getDoc,
  Timestamp
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { Client, DeceasedClient, IndividualService, ServicePlan, DocumentTemplate, FuneralHomeInfo, User } from "../types";

// Casos
export const getFirestoreCases = async (funeralHomeId: string): Promise<any[]> => {
    const q = query(collection(db, "cases"), where("funeralHomeId", "==", funeralHomeId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const addFirestoreCase = async (caseData: any, funeralHomeId: string) => {
    const id = caseData.id || caseData.caseId || `case-${Date.now()}`;
    const docRef = doc(db, "cases", id);
    await setDoc(docRef, {
        ...caseData,
        id,
        funeralHomeId,
        updatedAt: Timestamp.now()
    }, { merge: true });
    return { id };
};

// Clientes
export const getFirestoreClients = async (funeralHomeId: string): Promise<Client[]> => {
    const q = query(collection(db, "clients"), where("funeralHomeId", "==", funeralHomeId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Client));
};

export const addFirestoreClient = async (clientData: any, funeralHomeId: string) => {
    const dni = clientData.dniNumero || clientData.dni;
    const id = clientData.id || (dni ? `client-${dni}` : `client-${Date.now()}`);
    const docRef = doc(db, "clients", id);
    await setDoc(docRef, {
        ...clientData,
        id,
        funeralHomeId,
        updatedAt: Timestamp.now()
    }, { merge: true });
    return { id };
};

export const deleteFirestoreClient = async (clientId: string) => {
    return await deleteDoc(doc(db, "clients", clientId));
};

// Fallecidos
export const getFirestoreDeceased = async (funeralHomeId: string): Promise<DeceasedClient[]> => {
    const q = query(collection(db, "deceased"), where("funeralHomeId", "==", funeralHomeId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DeceasedClient));
};

export const addFirestoreDeceased = async (deceasedData: any, funeralHomeId: string) => {
    const dni = deceasedData.dniNumero || deceasedData.dni;
    const id = deceasedData.id || (dni ? `deceased-${dni}` : `deceased-${Date.now()}`);
    const docRef = doc(db, "deceased", id);
    await setDoc(docRef, {
        ...deceasedData,
        id,
        funeralHomeId,
        updatedAt: Timestamp.now()
    }, { merge: true });
    return { id };
};

export const deleteFirestoreDeceased = async (deceasedId: string) => {
    return await deleteDoc(doc(db, "deceased", deceasedId));
};

// Catálogo (Servicios y Planes)
export const getFirestoreServices = async (funeralHomeId: string): Promise<IndividualService[]> => {
    const q = query(collection(db, "services"), where("funeralHomeId", "==", funeralHomeId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as IndividualService));
};

export const getFirestorePlans = async (funeralHomeId: string): Promise<ServicePlan[]> => {
    const q = query(collection(db, "plans"), where("funeralHomeId", "==", funeralHomeId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ServicePlan));
};
export const addFirestoreService = async (service: IndividualService, funeralHomeId: string) => {
    const docRef = service.id.startsWith('s-') ? doc(collection(db, "services")) : doc(db, "services", service.id);
    const id = service.id.startsWith('s-') ? docRef.id : service.id;
    return await setDoc(docRef, { ...service, id, funeralHomeId }, { merge: true });
};

export const deleteFirestoreService = async (serviceId: string) => {
    return await deleteDoc(doc(db, "services", serviceId));
};

export const addFirestorePlan = async (plan: ServicePlan, funeralHomeId: string) => {
    const docRef = plan.id.startsWith('p-') ? doc(collection(db, "plans")) : doc(db, "plans", plan.id);
    const id = plan.id.startsWith('p-') ? docRef.id : plan.id;
    return await setDoc(docRef, { ...plan, id, funeralHomeId }, { merge: true });
};

export const deleteFirestorePlan = async (planId: string) => {
    return await deleteDoc(doc(db, "plans", planId));
};

// Plantillas
export const getFirestoreTemplates = async (funeralHomeId: string): Promise<DocumentTemplate[]> => {
    const q = query(collection(db, "templates"), where("funeralHomeId", "==", funeralHomeId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DocumentTemplate));
};

export const addFirestoreTemplate = async (template: DocumentTemplate, funeralHomeId: string) => {
    const docRef = doc(db, "templates", template.id);
    return await setDoc(docRef, { ...template, funeralHomeId }, { merge: true });
};

export const deleteFirestoreTemplate = async (templateId: string) => {
    const docRef = doc(db, "templates", templateId);
    return await deleteDoc(docRef);
};

// Configuración (Per Funeral Home)
export const getFirestoreSettings = async (funeralHomeId: string): Promise<any | null> => {
    const docRef = doc(db, "settings", funeralHomeId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? docSnap.data() : null;
};

export const updateFirestoreSettings = async (settingsData: any, funeralHomeId: string) => {
    const docRef = doc(db, "settings", funeralHomeId);
    return await setDoc(docRef, { ...settingsData, funeralHomeId }, { merge: true });
};

// Usuarios (Equipo)
export const getFirestoreUsers = async (funeralHomeId: string): Promise<User[]> => {
    const q = query(collection(db, "users"), where("funeralHomeId", "==", funeralHomeId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as User));
};

export const saveFirestoreUser = async (userData: User) => {
    const docRef = doc(db, "users", userData.id);
    return await setDoc(docRef, userData, { merge: true });
};
